import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Spinner,
} from '@monksflow/monks-ui';
import { ChevronDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useWorkspaceTagSuggestions } from '@/entities/project-tag';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useUpdateProjectFileTags } from '@/features/manage-project-files/api/useUpdateProjectFileTags.mutation';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type { EditFileTagsModalProps } from './EditFileTagsModal.types';
import { buildEditFileTagsFormSchema, type EditFileTagsFormData } from './edit-file-tags.schema';
import { useTranslation } from 'react-i18next';

export function EditFileTagsModal(props: EditFileTagsModalProps) {
  const { t } = useTranslation('projects');

  const { open, projectId, file, onClose, onSuccess } = props;

  const workspaceId = useCurrentWorkspaceId();
  const { suggestions } = useWorkspaceTagSuggestions(workspaceId);

  const defaultValues = useMemo<EditFileTagsFormData>(
    () => ({ tags: file?.tags.map((tag) => tag.name) ?? [] }),
    [file],
  );

  const formSchema = useMemo(() => buildEditFileTagsFormSchema(t), [t]);

  const form = useForm<EditFileTagsFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
  });

  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [defaultValues, form, open]);

  const tagsAnchorRef = useRef<HTMLDivElement | null>(null);

  const updateMutation = useUpdateProjectFileTags({ projectId });
  const isPending = updateMutation.isPending;

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const onSubmit = form.handleSubmit((formData) => {
    if (!file) return;
    // Skip the round-trip when the tag set is identical (order-independent).
    const nextSet = new Set(formData.tags);
    const currentSet = new Set(file.tags.map((tag) => tag.name));
    const unchanged =
      nextSet.size === currentSet.size && [...nextSet].every((name) => currentSet.has(name));
    if (unchanged) {
      handleClose();
      return;
    }
    updateMutation.mutate(
      { fileId: file.id, tags: formData.tags },
      {
        onSuccess: (updated) => {
          notificationService.showSuccess(t('manageProjectFiles.editFileTagsModal.tagsUpdated'));
          onSuccess?.(updated);
          handleClose();
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('manageProjectFiles.editFileTagsModal.failedToUpdateTags'),
          );
          notificationService.showError(detail);
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('manageProjectFiles.editFileTagsModal.editTags')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col"
          >
            <DialogBody className="flex flex-col gap-4 py-10">
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => {
                  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== 'Enter') return;
                    const target = event.currentTarget;
                    const text = target.value.trim();
                    if (text === '') return;
                    if (field.value.includes(text)) {
                      target.value = '';
                      return;
                    }
                    event.preventDefault();
                    field.onChange([...field.value, text]);
                    target.value = '';
                  };

                  return (
                    <FormItem>
                      <FormLabel>{t('manageProjectFiles.editFileTagsModal.tags')}</FormLabel>
                      <FormControl>
                        <Combobox<string, true>
                          multiple
                          value={field.value}
                          onValueChange={field.onChange}
                          items={suggestions}
                          disabled={isPending}
                          itemToStringLabel={(item) => item}
                          itemToStringValue={(item) => item}
                        >
                          <ComboboxChips ref={tagsAnchorRef}>
                            {field.value.map((tag, index) => (
                              <ComboboxChip
                                key={`${tag}:${index}`}
                                aria-label={t('manageProjectFiles.editFileTagsModal.removeTag', {
                                  tag,
                                })}
                                className="rounded-full bg-accent px-2.5 text-accent-foreground"
                              >
                                {tag}
                              </ComboboxChip>
                            ))}
                            <ComboboxChipsInput
                              placeholder={t('manageProjectFiles.editFileTagsModal.enterTags')}
                              disabled={isPending}
                              onKeyDown={handleKeyDown}
                            />
                            <ChevronDown
                              aria-hidden="true"
                              className="ml-auto size-4 shrink-0 text-foreground-subtle pointer-events-none"
                            />
                          </ComboboxChips>
                          <ComboboxContent anchor={tagsAnchorRef} className="pointer-events-auto">
                            <ComboboxList>
                              <ComboboxCollection>
                                {(item: string) => (
                                  <ComboboxItem key={item} value={item}>
                                    {item}
                                  </ComboboxItem>
                                )}
                              </ComboboxCollection>
                              <ComboboxEmpty>
                                {t('manageProjectFiles.editFileTagsModal.noResults')}
                              </ComboboxEmpty>
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                {t('manageProjectFiles.editFileTagsModal.cancel')}
              </Button>
              <Button type="submit" variant="default" disabled={isPending}>
                {isPending && <Spinner />} {t('manageProjectFiles.editFileTagsModal.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
